// ─── Stage / Outcome types ───────────────────────────────────────────────────

export type RLCaseStage =
  'Planning' | 'PreScheduling' | 'JobExecution' | 'JobCloseOut' | 'Resolved';

export type RLResolvedOutcome =
  | 'Resolved-Completed'
  | 'Resolved-PlanToPA'
  | 'Resolved-Divest'
  | 'Resolved-Error';

export type RLCancellationReason =
  'DuplicateOrHumanError' | 'PlanToPA' | 'WellDivested' | 'WellRecovered';

export type RLAttachmentType =
  | 'JobPlanPacket'
  | 'FinalReport'
  | 'ConservationStipulations'
  | 'SupportingPicsReports';

// ─── List / Page ─────────────────────────────────────────────────────────────

export interface RLCaseListQuery {
  page?: number;
  pageSize?: number;
  stage?: RLCaseStage;
  sort?: string;
  sortDescending?: boolean;
  /** Case-insensitive contains filter matched against case number or well name. */
  keyword?: string;
  /** When true, excludes RL cases that have already been sent to the Job Plan stage. */
  excludeSentToJobPlan?: boolean;
  /** When true, includes only RL cases already sent to the Job Plan stage. */
  onlySentToJobPlan?: boolean;
}

export interface RLCasePage {
  items: RLCaseListItem[];
  totalCount: number;
}

export interface RLCaseListItem {
  id: number;
  caseNumber: string;
  stage: RLCaseStage;
  statusWork: string | null;
  wellName: string | null;
  wellId: string | null;
  fot: string | null;
  jobType: string | null;
  jobReason: string | null;
  startDate: string | null;
  createdDate: string;
  updatedDate: string;
  hasPlanningData: boolean;
  companyCode: string | null;
  costCenter: string | null;
  plantCode: string | null;
  failureDate: string | null;
  avgBOED: number | null;
  isSimops: boolean;
  // Scheduling-view fields
  api14: string | null;
  preScheduleStatus: string | null;
  projectedJobDays: number | null;
  wellviewJobEntered: boolean;
  wellviewJobId: string | null;
  wellviewJobType: string | null;
  wellviewJobCreationDate: string | null;
  wellviewJobList: string | null;
  // Execution-view fields
  priorityDate: string | null;
  priorityWellComments: string | null;
  rigName: string | null;
  wellviewJobStartDateText: string | null;
  wellviewJobEndDateText: string | null;
  disableAutoProgression: boolean;
  isAirportPolygon?: boolean | null;
  // Well reference
  gaugerBeatCode: string | null;
  sectionName: string | null;
  // Conservation
  hasProtectedSpecies: boolean;
}

// ─── Conservation Status ────────────────────────────────────────────────────

export interface ConservationStatus {
  gisSummary: boolean;
  timingZoneOne: boolean;
  timingZoneTwo: boolean;
  duneLizardNm: boolean;
  duneLizardTx: boolean;
  hornshellMussel: boolean;
  blmLand: boolean;
  withinConservationDates: boolean;
}

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface RLCaseDetail {
  id: number;
  /** Shared Cases.Id — populated even when no RLCase row exists yet (id === 0). */
  caseId: number;
  caseNumber: string;
  stage: RLCaseStage;
  statusWork: string | null;
  wellId: string | null;
  fotId: number | null;
  cancellationReason: string | null;
  cancellationDetails: string | null;
  resolvedOutcome: string | null;
  createdDate: string;
  updatedDate: string;
  planningData: RLPlanningData | null;
  planningReview: RLPlanningReview | null;
  schedulingData: RLSchedulingData | null;
  closeOutData: RLCloseOutData | null;
  conservationStatus: ConservationStatus | null;
}

// ─── Sub-models ───────────────────────────────────────────────────────────────

export interface RLPlanningData {
  wellName: string | null;
  api14: string | null;
  liftMethod: string | null;
  field: string | null;
  state: string | null;
  area: string | null;
  fot: string | null;
  plantCode: string | null;
  companyCode: string | null;
  costCenter: string | null;
  latitude: number | null;
  longitude: number | null;
  failureDate: string | null;
  localReferenceNumber: string | null;
  wellTest1_BOED: number | null;
  wellTest2_BOED: number | null;
  wellTest3_BOED: number | null;
  avgBOED: number | null;
  wbs: string | null;
  jobType: string | null;
  jobReason: string | null;
  isSimops: boolean;
  comments: string | null;
}

export interface RLPlanningReview {
  projectedJobDays: number | null;
  isWellRigReady: boolean;
  preScheduleStatus: string | null;
  plannedCost: number | null;
  preScheduleComment: string | null;
  wellviewJobEntered: boolean;
  wellviewJobId: string | null;
  wellviewJobType: string | null;
  wellviewJobCreationDate: string | null;
  wellviewJobIdentifier: string | null;
  wellviewJobList: string | null;
}

export interface RLSchedulingData {
  rigId: number | null;
  rigName: string | null;
  isPrairieChickenPolygon: boolean | null;
  isAirportPolygon: boolean | null;
  electricalVariance: string | null;
  isSimops: boolean;
  api12: string | null;
  wellheadHeightInches: number | null;
  hasPriorityIssue: boolean;
  priorityDate: string | null;
  priorityWellComments: string | null;
  queueOrder: number | null;
  workflowDetermination: string | null;
  dpiRatio: number | null;
  bblsperM: number | null;
  production: number | null;
  gkScore: number | null;
  rigUp: string | null;
  projRigDown: string | null;
  projectedJobDays: number | null;
}

/** One row returned by GET /api/rigs/{id}/queue — used for the "Select Queue Order" table. */
export interface RLRigQueueRow {
  rlCaseId: number;
  caseNumber: string;
  wellName: string;
  queueOrder: number;
  stage: string;
  caseType: string;
  workflowDetermination: string | null;
  priorityDate: string | null;
  dpiRatio: number | null;
  bblsPerM: number | null;
  production: number | null;
  gkScore: number | null;
  rigUp: string | null;
  projRigDown: string | null;
  projectedJobDays: number | null;
}

/** One item sent to PUT /api/rigs/{id}/queue to batch-update QueueOrder in SchedulingData. */
export interface UpdateRigQueueItem {
  rlCaseId: number;
  queueOrder: number;
  caseType: string;
}

export interface RLCloseOutData {
  wellName: string | null;
  api14: string | null;
  fot: string | null;
  area: string | null;
  costCenter: string | null;
  state: string | null;
  plantCode: string | null;
  companyCode: string | null;
  latitude: number | null;
  longitude: number | null;
  localReferenceNumber: string | null;
  failureDate: string | null;
  oil_BOPD: number | null;
  water_BWPD: number | null;
  gas_MCFPD: number | null;
  failedComponentGroup: string | null;
  failedComponentType: string | null;
  failureLocation: string | null;
  failureDepth: number | null;
  componentDisposition: string | null;
  failureRemarks: string | null;
  failureAnalysisCompletionDate: string | null;
  primaryFailureClass: string | null;
  analysisRemarks: string | null;
  primaryFailureCause: string | null;
  rootFailureCause: string | null;
  failureSolution: string | null;
  failureObservations: string | null;
  preventativeMaintenance: string | null;
  actualJobCost: number | null;
  personPerformingTask: string | null;
  isFailure: boolean;
  responsiblePerson: string | null;
  jobType: string | null;
  jobReason: string | null;
  wellviewWellboreValidated: boolean | null;
}

// ─── Execution View ───────────────────────────────────────────────────────────

export interface WellviewJobOption {
  wellViewJobId: string;
  jobIdentifier: string;
  jobEndDateText: string | null;
  jobStartDateText: string | null;
  costToDate: number | null;
  differenceGross: number | null;
}

export interface RLExecutionView {
  caseId: number;
  caseNumber: string;
  wellviewJobId: string | null;
  /** `JobEndDateText` from the matching `WellviewJobs` row; non-null when WellView has recorded a job-end date. */
  wellviewJobEndDateText: string | null;
  // Wellview snapshot
  rigUpDate: string | null;
  jobEnd: string | null;
  costToDate: number | null;
  totalHours: number | null;
  differenceGross: number | null;
  wellviewLastSynced: string | null;
  wellviewDataIsStale: boolean;
  // Compliance confirmations
  // CH-RL-003 (3.2.4): confirmElectricalVariance, confirmSimops, wellviewWellboreUpdated
  // removed per requirements.md v1.21 FR-056.
  disableAutoProgression: boolean;
  rigDownDateInWellview: boolean;
  jobClosed: boolean;
  userIDOverride: string | null;
  // Artifact status (FR-057, DR-011)
  jobPlanPacketAvailable: boolean;
  finalReportAvailable: boolean;
  conservationStipulationsAvailable: boolean;
  // Failure Information
  failureGroup: string | null;
  failureReason: string | null;
  failureSize: string | null;
  failureDepth: string | null;
  failureType: string | null;
  failureCause: string | null;
  failureComment: string | null;
  // Daily reports
  dailyReports: RLDailyReport[];
  // Rig report job picker and data freshness
  wellviewJobOptions: WellviewJobOption[];
  wellviewDataLastPulledUtc: string | null;
}

export interface RLDailyReport {
  id: number;
  date: string;
  wsmName: string | null;
  totalHoursWorked: number | null;
  dailyCost: number | null;
  projectedOperations: string | null;
  hourlyRemarks: RLHourlyRemark[];
}

export interface RLHourlyRemark {
  id: number;
  activityStart: string | null;
  activityEnd: string | null;
  comments: string | null;
  duration: number | null;
}

// ─── Rig ─────────────────────────────────────────────────────────────────────

export interface RLRig {
  id: number;
  name: string;
  rigType: string | null;
  isAvailable: boolean;
}

// ─── Lookup ───────────────────────────────────────────────────────────────────

export interface RLLookupOption {
  id?: number;
  fieldKey?: string;
  value: string;
  parentFieldKey: string | null;
  parentValue: string | null;
  sortOrder: number;
  displayText?: string | null;
}

// ─── Plant Code ───────────────────────────────────────────────────────────────

export interface PlantCodeOption {
  plantCode: string;
  companyCode: string;
  description: string | null;
}

// ─── Attachment ───────────────────────────────────────────────────────────────

export interface RLAttachmentUrl {
  downloadUrl: string;
  fileName: string;
  urlExpiresAt: string;
}

export interface RLAttachmentMetadata {
  id: number;
  caseId: number;
  attachmentType: string;
  fileName: string;
  uploadedDate: string;
  fileSizeBytes: number;
  uploadedBy?: string | null;
}

// ─── Notification Log ─────────────────────────────────────────────────────────

export interface RLNotificationLogEntry {
  id: number;
  eventKey: string;
  subject: string;
  recipients: string;
  sentOnUtc: string;
  deliveryStatus: string;
  emlBlobPath?: string | null;
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

export interface RLAuditLogEntry {
  id: number;
  timestamp: string;
  actionType: string;
  actorName: string;
  details: string;
}

// ─── Well search (MWL — stub in M003) ────────────────────────────────────────

export interface RLWellSearchResult {
  wellId: string;
  wellName: string;
  api14: string;
}

// ─── Request models ───────────────────────────────────────────────────────────

export interface CreateRLCaseRequest {
  fotId: number;
  wellId?: string | null;
  planningData?: Partial<RLPlanningDataRequest> | null;
}

export interface RLPlanningDataRequest {
  wellName: string | null;
  api14: string | null;
  liftMethod: string | null;
  field: string | null;
  state: string | null;
  area: string | null;
  fot: string | null;
  plantCode: string | null;
  companyCode: string | null;
  costCenter: string | null;
  latitude: number | null;
  longitude: number | null;
  failureDate: string | null;
  localReferenceNumber: string | null;
  wellTest1_BOED: number | null;
  wellTest2_BOED: number | null;
  wellTest3_BOED: number | null;
  avgBOED: number | null;
  wbs: string | null;
  jobType: string | null;
  jobReason: string | null;
  isSimops: boolean;
  comments: string | null;
}

export interface UpdateRLPlanningRequest extends RLPlanningDataRequest {
  action?: string | null;
}

export interface SaveRLJobPlanRequest {
  // Well information
  wellName: string | null;
  api14: string | null;
  liftMethod: string | null;
  field: string | null;
  state: string | null;
  area: string | null;
  fot: string | null;
  plantCode: string | null;
  companyCode: string | null;
  costCenter: string | null;
  latitude: number | null;
  longitude: number | null;
  failureDate: string | null;
  localReferenceNumber: string | null;
  wellTest1_BOED: number | null;
  wellTest2_BOED: number | null;
  wellTest3_BOED: number | null;
  avgBOED: number | null;
  // Job information
  wbs: string | null;
  jobType: string | null;
  jobReason: string | null;
  isSimops: boolean;
  comments: string | null;
  // Job plan packet
  projectedJobDays: number | null;
  isWellRigReady: boolean;
  preScheduleStatus: string | null;
  plannedCost: number | null;
  preScheduleComment: string | null;
  wellviewJobEntered: boolean;
  wellviewJobId: string | null;
  wellviewJobType: string | null;
  wellviewJobList: string | null;
  wellviewJobCreationDate: string | null;
}

export interface UpdateRLReviewRequest {
  projectedJobDays: number | null;
  isWellRigReady: boolean;
  preScheduleStatus: string | null;
  plannedCost: number | null;
  preScheduleComment: string | null;
  wellviewJobEntered: boolean;
}

export interface UpdatePreScheduleStatusRequest {
  preScheduleStatus: string;
}

export interface LinkWellviewRequest {
  wellviewJobId: string;
  wellviewJobType?: string | null;
  wellviewJobList?: string | null;
  wellviewJobCreationDate?: string | null;
}

export interface WellViewJobResponse {
  jobIdentifier: string | null;
  wellViewJobId: string | null;
  jobType: string | null;
  wellViewJobStartDate: string | null;
}

export interface ScheduleRLCaseRequest {
  rigId: number;
  queueOrder?: number | null;
  isPrairieChickenPolygon?: boolean | null;
  isAirportPolygon?: boolean | null;
  electricalVariance?: string | null;
  isSimops: boolean;
  api12?: string | null;
  wellheadHeightInches?: number | null;
  hasPriorityIssue: boolean;
  priorityDate?: string | null;
  priorityWellComments?: string | null;
  workflowDetermination?: string | null;
  dpiRatio?: number | null;
  bblsperM?: number | null;
  production?: number | null;
  gkScore?: number | null;
  rigUp?: string | null;
  projRigDown?: string | null;
  projectedJobDays?: number | null;
}

export interface UpdateRLExecutionRequest {
  // CH-RL-003 (3.2.4): confirmElectricalVariance, confirmSimops, wellviewWellboreUpdated removed.
  disableAutoProgression: boolean;
  rigDownDateInWellview: boolean;
  jobClosed: boolean;
  userIDOverride: string | null;
  rigUpDate: string | null;
  jobEndDate: string | null;
  allowProgression: boolean;
  // WellView snapshot fields
  wellviewJobId: string | null;
  wellviewLastSynced: string | null;
  costToDate: number | null;
  totalHours: number | null;
  differenceGross: number | null;
  // Override flags
  rigUpDateOverriden: boolean;
  rigDownDateOverriden: boolean;
  // Failure Information
  failureGroup?: string | null;
  failureReason?: string | null;
  failureSize?: string | null;
  failureDepth?: string | null;
  failureType?: string | null;
  failureCause?: string | null;
  failureComment?: string | null;
}

export interface UpdateRLCloseOutRequest {
  wellName?: string | null;
  api14?: string | null;
  fot?: string | null;
  area?: string | null;
  costCenter?: string | null;
  state?: string | null;
  plantCode?: string | null;
  companyCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  localReferenceNumber?: string | null;
  failureDate?: string | null;
  oil_BOPD?: number | null;
  water_BWPD?: number | null;
  gas_MCFPD?: number | null;
  failedComponentGroup?: string | null;
  failedComponentType?: string | null;
  failureLocation?: string | null;
  failureDepth?: number | null;
  componentDisposition?: string | null;
  failureRemarks?: string | null;
  failureAnalysisCompletionDate?: string | null;
  primaryFailureClass?: string | null;
  analysisRemarks?: string | null;
  primaryFailureCause?: string | null;
  rootFailureCause?: string | null;
  failureSolution?: string | null;
  failureObservations?: string | null;
  preventativeMaintenance?: string | null;
  actualJobCost?: number | null;
  personPerformingTask?: string | null;
  isFailure: boolean;
  responsiblePerson?: string | null;
  jobType?: string | null;
  jobReason?: string | null;
  wellviewWellboreValidated: boolean | null;
}

export interface CancelRLCaseRequest {
  cancellationReason: string;
  cancellationDetails?: string | null;
}

export interface SendBackRLCaseRequest {
  rationale?: string | null;
  preScheduleStatus?: string | null;
  preScheduleComment?: string | null;
}

export interface CreateRLRigRequest {
  name: string;
  rigType?: string | null;
  isAvailable?: boolean;
}

export interface UpdateRLRigRequest {
  name: string;
  rigType?: string | null;
  isAvailable: boolean;
}

// ─── Lookup admin request models ─────────────────────────────────────────────

export interface CreateRLLookupOptionRequest {
  fieldKey: string;
  value: string;
  parentFieldKey?: string | null;
  parentValue?: string | null;
  sortOrder?: number;
}

export interface UpdateRLLookupOptionRequest {
  value: string;
  parentFieldKey?: string | null;
  parentValue?: string | null;
  sortOrder: number;
}

// ─── WellView TimeLog ─────────────────────────────────────────────────────────

export interface WellviewJobTimeLogDto {
  id: number;
  wellViewJobId: string;
  activityStartDate: string | null;
  activityEndDate: string | null;
  idWell: string;
  activityCode: string | null;
  durationHours: number | null;
  comments: string | null;
}

export interface WellviewJobPeriodDto {
  reportStartDate: string | null;
  reportEndDate: string | null;
  dailyExpenses: number | null;
  projectedOperations: string | null;
  wsmName: string | null;
}

export interface WellviewJobTimeLogEnvelopeDto {
  wellViewJobId: string;
  idWell: string | null;
  timeLogs: WellviewJobTimeLogDto[];
  periods: WellviewJobPeriodDto[];
}
